/**
 * Shared color utility functions.
 *
 * Used by drawMenu.ts, drawTimer.ts, and drawButtons.ts for
 * luminance-aware rendering (light vs dark text/icons on theme backgrounds).
 */

/**
 * Parse a CSS color string to RGB values. Supports hex (#RGB, #RRGGBB),
 * named colors ('black', 'white'), and returns [0,0,0] for unparseable input.
 */
export function parseColorToRGB(color: string): [number, number, number] {
    const c = color.trim().toLowerCase()
    if (c === 'black') return [0, 0, 0]
    if (c === 'white') return [255, 255, 255]
    if (c.startsWith('#')) {
        const hex = c.slice(1)
        if (hex.length === 3) {
            return [parseInt(hex[0] + hex[0], 16), parseInt(hex[1] + hex[1], 16), parseInt(hex[2] + hex[2], 16)]
        }
        if (hex.length >= 6) {
            return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)]
        }
    }
    return [0, 0, 0]
}

/**
 * Returns relative luminance (0–1) per WCAG formula.
 * Values < 0.5 are "dark", >= 0.5 are "light".
 */
function relativeLuminance(color: string): number {
    const [r, g, b] = parseColorToRGB(color).map((c) => {
        const s = c / 255
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Returns true if the given color is perceptually light (threshold 0.35). */
export function isLightBackground(color: string): boolean {
    return relativeLuminance(color) > 0.35
}
